import React from 'react';
import { TreeGridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-react-treegrid';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeLibraryBulkSelectionToolbar from './knowledgeLibrary/KnowledgeLibraryBulkSelectionToolbar';

const { useState, useEffect, useRef, useCallback, useMemo } = React;

interface HierarchyNode {
    UniqueID: string;
    ID: string;
    Name: string;
    Description: string;
    ParentID: string | null;
    IsTag: boolean;
    IsFavorite?: boolean;
    KnowledgeBook?: IKnowledgeBook;
    Children?: HierarchyNode[];
}

interface KnowledgeLibraryHierarchyProps {
    knowledgeBooks: IKnowledgeBook[];
    favoriteKnowledgeBookIds: string[];
    selectedKnowledgeBookIdSet: ReadonlySet<string>;
    classificationFilterTagIds?: ReadonlySet<string> | null;
    classificationSortDirection: 'asc' | 'desc';
    selectedFacility: IFacilityDetail | null;
    onBeginEdit: (args: any) => void;
    onCommandClick: (args: any) => void;
    onPreviewKnowledgeBook: (kb: IKnowledgeBook) => void | Promise<void>;
    onFavoriteClick: (kb: IKnowledgeBook) => void;
    onToggleKnowledgeBookSelection: (knowledgeBookId: string) => void;
    onClearSelection: () => void;
    onAssignSelectedTags: () => void | Promise<void>;
    onAddSelectedToFavorites: () => void | Promise<void>;
    onRemoveSelectedFromFavorites: () => void | Promise<void>;
    onDeleteSelectedKnowledgeBooks: () => void | Promise<void>;
    canModifyKnowledgeBook: (knowledgeBook: IKnowledgeBook) => boolean;
    canManageKnowledgeBook: (knowledgeBook: IKnowledgeBook) => boolean;
}

const KnowledgeLibraryHierarchy: React.FC<KnowledgeLibraryHierarchyProps> = ({
    knowledgeBooks,
    favoriteKnowledgeBookIds,
    selectedKnowledgeBookIdSet,
    classificationFilterTagIds = null,
    classificationSortDirection,
    selectedFacility,
    onBeginEdit,
    onCommandClick,
    onPreviewKnowledgeBook,
    onFavoriteClick,
    onToggleKnowledgeBookSelection,
    onClearSelection,
    onAssignSelectedTags,
    onAddSelectedToFavorites,
    onRemoveSelectedFromFavorites,
    onDeleteSelectedKnowledgeBooks,
    canModifyKnowledgeBook,
    canManageKnowledgeBook
}) => {
    const treeGridRef = useRef<TreeGridComponent | null>(null);
    const [treeData, setTreeData] = useState<HierarchyNode[]>([]);

    const favoriteKnowledgeBookIdSet = useMemo(() => new Set(favoriteKnowledgeBookIds), [favoriteKnowledgeBookIds]);
    const selectedCount = selectedKnowledgeBookIdSet.size;
    const selectedKnowledgeBooks = useMemo(() => {
        if (selectedCount === 0) {
            return [];
        }

        return knowledgeBooks.filter(knowledgeBook => selectedKnowledgeBookIdSet.has(knowledgeBook.ID));
    }, [knowledgeBooks, selectedCount, selectedKnowledgeBookIdSet]);

    const allSelectedAreFavorites = useMemo(() => {
        if (selectedCount === 0) {
            return false;
        }

        return selectedKnowledgeBooks.every(kb => favoriteKnowledgeBookIdSet.has(kb.ID));
    }, [favoriteKnowledgeBookIdSet, selectedCount, selectedKnowledgeBooks]);
    
    const noneSelectedAreFavorites = useMemo(() => {
        if (selectedCount === 0) {
            return true;
        }

        return selectedKnowledgeBooks.every(kb => !favoriteKnowledgeBookIdSet.has(kb.ID));
    }, [favoriteKnowledgeBookIdSet, selectedCount, selectedKnowledgeBooks]);

    const canModifySelectedKnowledgeBooks = useMemo(() => {
        return selectedCount > 0 && selectedKnowledgeBooks.every(canModifyKnowledgeBook);
    }, [canModifyKnowledgeBook, selectedCount, selectedKnowledgeBooks]);

    const canManageSelectedKnowledgeBooks = useMemo(() => {
        return selectedCount > 0 && selectedKnowledgeBooks.every(canManageKnowledgeBook);
    }, [canManageKnowledgeBook, selectedCount, selectedKnowledgeBooks]);

    const buildHierarchy = useCallback(() => {
        const taxonomyDetail = FacilityStore.getVisibleTaxonomyClassificationDetail();
        const classificationTags = taxonomyDetail?.ClassificationTags || [];
        const allowedClassificationTagIds = classificationFilterTagIds || null;

        // Build a map of tag ID to tag info
        const tagMap = new Map<string, IClassificationTag>();
        classificationTags.forEach(tag => {
            tagMap.set(tag.ID, tag);
        });

        // Build tree nodes for classification tags
        const tagNodes = new Map<string, HierarchyNode>();
        const rootNodes: HierarchyNode[] = [];

        // Create nodes for all classification tags
        classificationTags.forEach(tag => {
            const node: HierarchyNode = {
                UniqueID: 'tag_' + tag.ID,
                ID: tag.ID,
                Name: tag.Name,
                Description: '',
                ParentID: tag.ParentID ? 'tag_' + tag.ParentID : null,
                IsTag: true,
                Children: []
            };
            tagNodes.set(tag.ID, node);
        });

        // Build parent-child relationships for tags
        classificationTags.forEach(tag => {
            const node = tagNodes.get(tag.ID)!;
            if (tag.ParentID) {
                const parentNode = tagNodes.get(tag.ParentID);
                if (parentNode) {
                    parentNode.Children!.push(node);
                }
            } else {
                rootNodes.push(node);
            }
        });

        // Add knowledge books to their respective tags
        knowledgeBooks.forEach(kb => {
            const kbClassificationTags = kb.ClassificationTags || [];
            
            if (kbClassificationTags.length === 0) {
                return;
            } else {
                // Add to each classification tag
                kbClassificationTags.forEach(tagId => {
                    if (allowedClassificationTagIds && !allowedClassificationTagIds.has(tagId)) {
                        return;
                    }

                    const tagNode = tagNodes.get(tagId);
                    if (tagNode) {
                        const kbNode: HierarchyNode = {
                            UniqueID: 'kb_' + tagId + '_' + kb.ID,
                            ID: kb.ID,
                            Name: kb.Name,
                            Description: kb.Description || '',
                            ParentID: 'tag_' + tagId,
                            IsTag: false,
                            IsFavorite: favoriteKnowledgeBookIds.indexOf(kb.ID) >= 0,
                            KnowledgeBook: kb
                        };
                        tagNode.Children!.push(kbNode);
                    }
                });
            }
        });

        // Sort children by Name at each level
        const sortChildren = (nodes: HierarchyNode[]) => {
            nodes.sort((a, b) => {
                // Tags come before knowledge books
                if (a.IsTag !== b.IsTag) {
                    return a.IsTag ? -1 : 1;
                }
                const comparison = a.Name.localeCompare(b.Name);
                return classificationSortDirection === 'asc' ? comparison : -comparison;
            });
            nodes.forEach(node => {
                if (node.Children && node.Children.length > 0) {
                    sortChildren(node.Children);
                }
            });
        };

        // Remove tags that have no knowledge books (directly or in descendants)
        const hasKnowledgeBooks = (node: HierarchyNode): boolean => {
            if (!node.IsTag) return true; // Knowledge book nodes always count
            if (!node.Children || node.Children.length === 0) return false;
            return node.Children.some(child => hasKnowledgeBooks(child));
        };

        const filterEmptyTags = (nodes: HierarchyNode[]): HierarchyNode[] => {
            return nodes.filter(node => {
                if (!node.IsTag) return true; // Keep all knowledge books
                if (!hasKnowledgeBooks(node)) return false; // Remove empty tags
                // Recursively filter children
                node.Children = filterEmptyTags(node.Children || []);
                return true;
            });
        };

        const filteredRootNodes = filterEmptyTags(rootNodes);

        // Sort root nodes and their children
        sortChildren(filteredRootNodes);

        setTreeData(filteredRootNodes);
    }, [classificationFilterTagIds, classificationSortDirection, knowledgeBooks, favoriteKnowledgeBookIds]);

    useEffect(() => {
        buildHierarchy();
    }, [buildHierarchy]);

    // Use native TreeGrid API to update data - bypasses React wrapper issues with template columns
    useEffect(() => {
        if (treeGridRef.current) {
            const grid = treeGridRef.current;
            grid.dataSource = treeData;
            grid.refreshColumns();
            grid.refresh();
        }
    }, [treeData]);

    // Handle dataBound event to ensure templates are rendered after data loads
    const handleDataBound = useCallback(() => {
        // Force refresh of template columns if they appear empty
        if (treeGridRef.current) {
            const grid = treeGridRef.current;
            const rows = grid.getRows();
            if (rows && rows.length > 0) {
                const firstCell = rows[0].querySelector('.kb-tag-name, .kb-name');
                if (!firstCell) {
                    // Templates didn't render, force refresh
                    grid.refreshColumns();
                }
            }
        }
    }, []);

    const handleViewClick = useCallback((rowData: HierarchyNode) => {
        const args = {
            commandColumn: { type: 'View', title: InovxLoader.t('facility:preferences.viewButton') },
            rowData: rowData.KnowledgeBook
        };
        onCommandClick(args);
    }, [onCommandClick]);

    const handlePreviewClick = useCallback((rowData: HierarchyNode) => {
        if (rowData.KnowledgeBook) {
            onPreviewKnowledgeBook(rowData.KnowledgeBook);
        }
    }, [onPreviewKnowledgeBook]);

    const handleEditClick = useCallback((rowData: HierarchyNode) => {
        if (!rowData.KnowledgeBook || !canModifyKnowledgeBook(rowData.KnowledgeBook)) {
            return;
        }

        const args = {
            requestType: 'beginEdit',
            rowData: rowData.KnowledgeBook
        };
        onBeginEdit(args);
    }, [onBeginEdit]);

    const handleDeleteClick = useCallback((rowData: HierarchyNode) => {
        if (!rowData.KnowledgeBook || !canManageKnowledgeBook(rowData.KnowledgeBook)) {
            return;
        }

        const args = {
            requestType: 'delete',
            data: [rowData.KnowledgeBook]
        };
        onBeginEdit(args);
    }, [canManageKnowledgeBook, onBeginEdit]);

    const handleFavoriteClick = useCallback((rowData: HierarchyNode) => {
        onFavoriteClick(rowData.KnowledgeBook);
    }, [onFavoriteClick]);

    const renderSelectionColumn = useCallback((props: any) => {
        if (props.IsTag) {
            return null;
        }

        const rowData = props as HierarchyNode;
        const isSelected = selectedKnowledgeBookIdSet.has(rowData.ID);
        return (
            <div className="kb-selection-cell">
                <input
                    type="checkbox"
                    className="kb-selection-checkbox"
                    checked={isSelected}
                    aria-label={InovxLoader.t('facility:knowledgeBook.selectedCount', { count: 1 })}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                        event.stopPropagation();
                        onToggleKnowledgeBookSelection(rowData.ID);
                    }}
                />
            </div>
        );
    }, [onToggleKnowledgeBookSelection, selectedKnowledgeBookIdSet]);

    const renderNameColumn = useCallback((props: any) => {
        if (props.IsTag) {
            return (
                <span className="kb-tag-name" title={props.Name}>
                    <span className="kb-icon kb-tag-icon"></span>
                    <span className="kb-text-overflow kb-tag-label">{props.Name}</span>
                </span>
            );
        }

        return (
            <span className="kb-name" title={props.Name}>
                {renderSelectionColumn(props)}
                <span className="kb-icon kb-book-icon"></span>
                <span className="kb-text-overflow">{props.Name}</span>
            </span>
        );
    }, [renderSelectionColumn]);

    const renderDescriptionColumn = useCallback((props: any) => {
        return (
            <span className="kb-text-overflow" title={props.Description}>{props.Description}</span>
        );
    }, []);

    const renderActionHeader = useCallback(() => {
        return (
            <div className="knowledge-library-grid-header-actions">
                {/* Keep the toolbar props local to the grid so header templates read the latest selection state. */}
                <KnowledgeLibraryBulkSelectionToolbar
                    key={selectedCount}
                    selectedCount={selectedCount}
                    allSelectedAreFavorites={allSelectedAreFavorites}
                    noneSelectedAreFavorites={noneSelectedAreFavorites}
                    canModifySelectedKnowledgeBooks={canModifySelectedKnowledgeBooks}
                    canManageSelectedKnowledgeBooks={canManageSelectedKnowledgeBooks}
                    onClearSelection={onClearSelection}
                    onAssignTags={onAssignSelectedTags}
                    onAddToFavorites={onAddSelectedToFavorites}
                    onRemoveFromFavorites={onRemoveSelectedFromFavorites}
                    onDeleteKnowledgeBooks={onDeleteSelectedKnowledgeBooks}
                />
            </div>
        );
    }, [allSelectedAreFavorites, canManageSelectedKnowledgeBooks, canModifySelectedKnowledgeBooks, noneSelectedAreFavorites, onAddSelectedToFavorites, onAssignSelectedTags, onClearSelection, onDeleteSelectedKnowledgeBooks, onRemoveSelectedFromFavorites, selectedCount]);

    useEffect(() => {
        if (treeGridRef.current) {
            const grid = treeGridRef.current;
            grid.refreshColumns();
            grid.refresh();
        }
    }, [canManageKnowledgeBook, canModifyKnowledgeBook, renderActionHeader, renderNameColumn]);

    const renderActionColumn = useCallback((props: any) => {
        if (props.IsTag) {
            return null;
        }
        const rowData = props as HierarchyNode;
        const isFavorite = rowData.IsFavorite === true;
        const canModifyBook = !!rowData.KnowledgeBook && canModifyKnowledgeBook(rowData.KnowledgeBook);
        const canManageBook = !!rowData.KnowledgeBook && canManageKnowledgeBook(rowData.KnowledgeBook);
        
        return (
            <div className="kb-action-buttons">
                <button className="e-btn e-flat kb-action-preview" title={InovxLoader.t('facility:knowledgeBook.preview')} onClick={(e) => { e.stopPropagation(); handlePreviewClick(rowData); }}>
                    <span className="e-btn-icon e-v-preview e-v-icons e-icons"></span>
                </button>
                <button className="e-btn e-flat kb-action-view" title={InovxLoader.t('facility:knowledgeBook.open')} onClick={(e) => { e.stopPropagation(); handleViewClick(rowData); }}>
                    <span className="e-btn-icon e-v-view e-v-icons e-icons"></span>
                </button>
                <button
                    className={isFavorite ? 'e-btn e-flat kb-action-favorite active' : 'e-btn e-flat kb-action-favorite'}
                    title={isFavorite ? InovxLoader.t('facility:knowledgeBook.removeFavorite') : InovxLoader.t('facility:knowledgeBook.addFavorite')}
                    onClick={(e) => { e.stopPropagation(); handleFavoriteClick(rowData); }}>
                    <span className={`e-btn-icon fa ${isFavorite ? 'fa-star' : 'fa-star-o'}`}></span>
                </button>
                {(canModifyBook || canManageBook) && (
                    <button className="e-btn e-flat kb-action-edit" title={InovxLoader.t('facility:preferences.editButton')} onClick={(e) => { e.stopPropagation(); handleEditClick(rowData); }}>
                        <span className="e-btn-icon e-v-edit e-v-icons e-icons"></span>
                    </button>
                )}
                {canManageBook && (
                    <button className="e-btn e-flat kb-action-delete" title={InovxLoader.t('facility:form.delete')} onClick={(e) => { e.stopPropagation(); handleDeleteClick(rowData); }}>
                        <span className="e-btn-icon e-v-delete e-v-icons e-icons"></span>
                    </button>
                )}
            </div>
        );
    }, [canManageKnowledgeBook, canModifyKnowledgeBook, handleFavoriteClick, handlePreviewClick, handleViewClick, handleEditClick, handleDeleteClick]);

    return (
        <div className="knowledge-library-hierarchy" style={{ position: 'relative' }}>
            <div className="table-selection-filter">
                {renderActionHeader()}
            </div>
            <TreeGridComponent
                ref={treeGridRef}
                dataSource={treeData}
                childMapping='Children'
                treeColumnIndex={0}
                allowSorting={false}
                allowPaging={false}
                allowSelection={false}
                showColumnChooser={false}
                dataBound={handleDataBound}
            >
                <ColumnsDirective>
                    <ColumnDirective field='Name' width='30%' headerText={InovxLoader.t('facility:knowledgeBook.knowledgeBook')} template={renderNameColumn}></ColumnDirective>
                    <ColumnDirective field='Description' headerText={InovxLoader.t('facility:knowledgeBook.knowledgeBookDescription')} template={renderDescriptionColumn} allowFiltering={false} allowSorting={false}></ColumnDirective>
                    <ColumnDirective 
                    headerText="" width='220' 
                    template={renderActionColumn} 
                    // headerTemplate={renderActionHeader} 
                    allowFiltering={false} allowSorting={false} />
                </ColumnsDirective>
            </TreeGridComponent>
        </div>
        
    );
};

export default React.memo(KnowledgeLibraryHierarchy);
